import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { ISigningTextsInterface, SigningTexts } from '@app/certificates/signing/signing';
import { SigningService } from '@app/certificates/signing/signing.service';
import { EmergencyService } from '@app/core/auth/component/emergency-login/emergency-login.service';
import { InfoService } from '@app/core/info/info.service';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { IFileDataOTC } from '../business-models';
import { BusinessService } from '../business.service';
import { VisualSignaturePositionComponent } from '../visual-signature-position/visual-signature-position.component';
import { SessionStorageService } from '@app/core/local-storage/local-storage.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  selector: 'cloud-business-signing-otc',
  templateUrl: './business-signing-otc.component.html',
  styleUrls: ['./business-signing-otc.component.scss']
})
export class BusinessSigningOtcComponent implements OnInit {
  @ViewChild('stepper') stepper: MatStepper;
  
  readonly dateTimeFormat = environment.dateTimeFormat;
  readonly expirationTime = environment.signedDocExpirationTime;

  validCertificates:boolean = true;
  texts: ISigningTextsInterface = new SigningTexts();
  files: IFileDataOTC[] = [];

  users: any;
  loadingResend = true;
  loadingNames = true;

  showValidations = false;
  packageToArchive = true;

  isRegistered = false;
  sendWithoutGroups: boolean = false;

  isResend = false;
  visibleSignature = true;
  sendSignedDocumentsByEmail = false;
  
  loading: boolean = false;
  isSentSuccessfully: boolean = false;
  signingStepCompleted: boolean = false;
  selectedStepIndex: number = 0;

  resultId: any;
  websessionId: any;
  signsessionId: any;
  status: any;
  
  private signatureDialog!: MatDialogRef<VisualSignaturePositionComponent>;

  constructor(
    public info: InfoService,
    private signingService: SigningService,
    private translateService: TranslateService,
    private cd: ChangeDetectorRef,
    private router: Router,
    private emergency: EmergencyService,
    private dialog: MatDialog,
    private businessService: BusinessService,
    private sessionStorageService: SessionStorageService,
  ) { }

  ngOnInit() {
    if (this.router.getCurrentNavigation() && this.router?.getCurrentNavigation()?.extras?.state) {
      this.isResend = true;
    } else {
      this.loadingResend = false;
      this.loadingNames = false;
    }
    if (this.emergency.getEmergencyState()) {
      this.router.navigate(['/manage/manage']);
    }
    this.loadTranslations();


    this.info.dismissAll();

    const navRow = this.router.getCurrentNavigation()?.extras?.state?.['row']
      ?? history.state?.row;
    if (navRow?.result) {
      this.applyIdentificatorData(navRow);
    }
  }

  private applyIdentificatorData(data: any) {
    this.websessionId = data.result.webssionId   // backend typo: webssionId (double-s)
    this.signsessionId = data.result.signsessionId
    this.resultId = data.result.id
    this.users = data.name
    this.status = data.status
  }

  checkLoadedResend() {
    return !this.loadingResend && !this.loadingNames;
  }

  ngAfterViewChecked() {
    this.cd.detectChanges();
  }

  isIE() {
    const match = navigator.userAgent.search(/(?:Edge|MSIE|Trident\/.*; rv:)/);
    let isIE = false;
    if (match !== -1) {
      isIE = true;
    }
    return isIE;
  }

  private loadTranslations(): void {
    const i18nTexts = 'signingTexts';
    this.translateService.stream([i18nTexts], {})
      .subscribe((texts: any) => {
        [this.texts] = [texts[i18nTexts]];
      });
  }

  fileBrowseHandler(files:any) {
    Array.from(files).forEach(file => {
      let isComplient = this.checkFileLimitations(file);
      if (isComplient) {
        this.files.push(this.initFileEntry(file));
      }
    });
  }

  deleteFile(index: number) {
    this.files.splice(index, 1);
  }

  checkFileLimitations(file:any) {
    let isComplient = true;
    let totalSize = 0;
    this.files.forEach(file2 => {
      if (file2.file) {
        totalSize += file2.file.size;
      }
    })

    if (file.size > 5000000) {
      isComplient = false;
      this.info.error('businessTexts.fileLimit');
    }
    if (this.files.length >= 10) {
      isComplient = false;
      this.info.error('businessTexts.fileNumberLimit');
    }
    if (totalSize + file.size > 50000000) {
      isComplient = false;
      this.info.error('businessTexts.totalFileLimit');
    }
    let isPdf = file.type.split('/')[1] === 'pdf'
    if (!isPdf){
      isComplient = false;
      this.info.error('businessTexts.unsupportedFileType');
    }
    let found = this.files.find(f =>
      f.fileName == file.name
    );
    if (found) {
      isComplient = false;
      this.info.error('businessTexts.alreadyUploadedFile');
    }
    return isComplient;
  }

  initFileEntry(file:any) {
    let fileEntry: IFileDataOTC = {
      file: file,
      fileName: file.name,
      contentAsBase64: '',
      padesVisualSignature: file.type == 'application/pdf' ? true : false,
      signaturePosition: null,
      signable: true,
      legalFileName: '',
      toBeArchived: this.packageToArchive,
    };
    this.getBase64(fileEntry);
    return fileEntry;
  }

  viewFile(file:any) {
    const fileBlob = new Blob([file], { type: file.type });
    const fileURL = URL.createObjectURL(fileBlob);
    window.open(fileURL, '_blank');
  }

  onCancel() {
    this.clearPackageData();
    this.router.navigate(['business/identification'], { state: { isFromOtc: true } });
  }

  clearPackageData() {
    this.isResend = false;
    this.users = '';
    this.files = [];
    this.packageToArchive = true;
    this.showValidations = false;
    this.loading = false;
    this.isSentSuccessfully = false;
    this.signingStepCompleted = false;
    this.selectedStepIndex = 0;
  }

  onSend() {
    const cmrId = this.sessionStorageService.getItem("currentCompany").cmrId
    this.showValidations = true;

    let request = {
      documents: this.getContent(),
      sendSignedDocumentsByEmail: this.sendSignedDocumentsByEmail,
    }
    if (!this.isRegistered) {
      if (this.files.length && this.hasSignableFile()) {
        this.signingStepCompleted = true;
        this.stepper.selected.completed = true;
        this.stepper.next();
        this.loading = true;
        
        if (this.status == 'CREATE_SIGN_SESSION_REQUEST') {
          const sessionId = this.websessionId
            this.signingService.sendPackageForSigningOTC(cmrId, request, sessionId, this.resultId, this.status).subscribe(res => {
            if (res.status === 200) {
              this.info.success('businessTexts.documentSentSuccess');
              this.isSentSuccessfully = true;
            } else {
              this.info.error('signingTexts.signingError');
              this.isSentSuccessfully = false;
            }
            this.loading = false;
          })
        } else if (this.status == 'SIGN_SESSION') {
          const sessionId = this.signsessionId
          this.signingService.sendPackageForSigningOTC(cmrId, request, sessionId, this.resultId, this.status).subscribe(res => {
            if (res.status === 200) {
              this.info.success('businessTexts.documentSentSuccess');
              this.isSentSuccessfully = true;
            } else {
              this.info.error('signingTexts.signingError');
              this.isSentSuccessfully = false;
            }
            this.loading = false;
          })
        }
      }
    } else {
      this.info.error('businessTexts.notRegistered');
    }
  }

  hasSignableFile() {
    return this.files.find(f => f.signable);
  }

  getContent(): any[] {
    return this.files.map((f) => {
      const document: any = {
        contentAsBase64: f.contentAsBase64,
        fileName: f.fileName,
        legalFileName: f.legalFileName || f.fileName,
        padesVisualSignature: f.signable && this.visibleSignature && f.padesVisualSignature,
        toBeArhieved: f.toBeArchived || false
      };
      if (f.signable && f.padesVisualSignature && this.isPdf(f)) {
        if (f.signaturePosition && Array.isArray(f.signaturePosition) && f.signaturePosition.length > 0) {
          const position = f.signaturePosition[0];
          document.signaturePosition = {
            imageHeight: this.convertPxToPoints(position.height || position.imageHeight || 100),
            imageWidth: this.convertPxToPoints(position.width || position.imageWidth || 200),
            imageXAxis: this.convertPxToPoints(position.x || position.imageXAxis || 200),
            imageYAxis: this.convertPxToPoints(position.y || position.imageYAxis || 200),
            pageNumber: position.page || position.pageNumber || 1
          };
        } else {
          document.signaturePosition = {
            imageHeight: 100,
            imageWidth: 200,
            imageXAxis: 200,
            imageYAxis: 200,
            pageNumber: 1
          };
        }
      }

      return document;
    });
  }

  convertPxToPoints(d:number) {
    if (Math.floor(d * 0.74999943307122) == 0) {
      return 1;
    }
    return Math.floor(d * 0.74999943307122);
  }

  getBase64(f:any) {
    const reader = new FileReader();
    reader.readAsDataURL(f.file);
    reader.onload = () => {
      f.contentAsBase64 = reader?.result?.toString().split(',')[1];
    };
  }

  compareObjects(o1: any, o2: any) {
    return o1.id == o2.id;
  }

  openPositionDialog(pdf:any, index:number) {
    pdf.index = index;
    this.openPositionPanel(pdf);
  }

  isPdf(f:any) {
    return f.file.type == 'application/pdf';
  }

  isSignable(f:any) {
    return this.visibleSignature && f.signable;
  }

  isMainPosButtonDisabled() {
    return !this.visibleSignature || !this.hasPdf();
  }

  hasPdf() {
    return this.files.find((f:any) => f.file.type == 'application/pdf' && f.signable);
  }

  openPositionPanel(file?: any) {
    if (this.users && this.users.bg) {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.disableClose = false;
      dialogConfig.autoFocus = true;
      dialogConfig.minWidth = '50%';
      dialogConfig.minHeight = 'calc(100vh - 90px)';
      dialogConfig.width = 'auto';
      dialogConfig.height = 'auto';
      let pdfs: any[] = []
      this.files.forEach((f, i) => {
        if (this.isPdf(f) && f.signable) {
          f.index = i
          pdfs.push(f);
        }
      })
      dialogConfig.data = {
        files: file ? [file] : pdfs,
        signers: this.users,
        webIdentificationOtc: true,
      };
      dialogConfig.panelClass = 'position-panel-class';
      this.signatureDialog = this.dialog.open(VisualSignaturePositionComponent, dialogConfig);

      const sub = this.signatureDialog.componentInstance.close.subscribe((updatedFiles: any) => {
        if (updatedFiles) {
          updatedFiles.forEach((file: any) => {
            this.files[file.index] = file;
          })
        }
        this.signatureDialog.close();
      });
    } else {
      this.info.error('businessTexts.noUsers');
    }

  }
  sendWithoutGroupsChecked(event: any) {
    this.sendWithoutGroups = event.checked;
  }
}
